/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);
      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();
      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    open(opener, elementToFocus) {
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: this.errorMsg?.textContent }
        }));
      }
    }

    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}


(function () {
  'use strict';

  if (window.giftSystemActive) {
    console.warn('⚠️ Gift system already running — duplicate load prevented');
    return;
  }
  window.giftSystemActive = true;
  console.log('[GiftSystem] ✅ Initialized');

  let isProcessingGifts = false;
  let previousUnlockStates = { standard: false, express: false, gift: false };
  let pollInterval = null;
  let updateInProgress = false;
  let disableInProgress = false;
  let cachedCart = null;
  let lastFetch = 0;
  let debounceTimer = null;
  let pendingForce = false;
  let pendingRecheck = false;
  let currentProgress = 0;
  const CACHE_DURATION = 500;
  const UPDATE_DEBOUNCE = 300;

  const ICONS = {
    standard: 'https://cdn.shopify.com/s/files/1/0498/1194/7675/files/sb-standard-shipping-icon.svg',
    express:  'https://cdn.shopify.com/s/files/1/0498/1194/7675/files/sb-express-shipping-icon.svg',
    gift:     'https://cdn.shopify.com/s/files/1/0498/1194/7675/files/sb-free-gift-icon.svg',
    unlocked: 'https://cdn.shopify.com/s/files/1/0498/1194/7675/files/sb-gift-unlocked-icon.svg'
  };

  function getNotice() {
    return document.querySelector('#cart-incentive-notice') ||
           document.querySelector('.free-shipping-notice');
  }

  async function getCart(useCache = true) {
    const now = Date.now();
    if (useCache && cachedCart && (now - lastFetch) < CACHE_DURATION) {
      console.log('[GiftSystem] getCart: using cache');
      return cachedCart;
    }
    console.log('[GiftSystem] getCart: fetching fresh');
    const res = await fetch('/cart.js', { cache: 'no-store' });
    cachedCart = await res.json();
    lastFetch = now;
    console.log('[GiftSystem] getCart: item_count=' + cachedCart.item_count + ' total=' + (cachedCart.total_price/100));
    return cachedCart;
  }

  function getGiftRules(notice) {
    try {
      const raw = notice.dataset.giftRules;
      if (!raw) {
        console.warn('[GiftSystem] getGiftRules: data-gift-rules attribute is EMPTY');
        return [];
      }
      const parsed = JSON.parse(raw);
      console.log('[GiftSystem] getGiftRules: parsed ' + parsed.length + ' rules', parsed);
      return parsed;
    } catch (e) {
      console.error('[GiftSystem] getGiftRules: JSON parse FAILED', e);
      return [];
    }
  }

  function itemMatchesRule(item, rule) {
    const hasVendors = rule.vendors && rule.vendors.length > 0;
    const hasProductTypes = rule.product_types && rule.product_types.length > 0;
    const hasSpecificProducts = rule.specific_product_ids && rule.specific_product_ids.length > 0;

    if (!hasVendors && !hasProductTypes && !hasSpecificProducts) return true;

    if (hasVendors) {
      const itemVendor = (item.vendor || item.product_vendor || '').toLowerCase().trim();
      if (!rule.vendors.includes(itemVendor)) return false;
    }

    if (hasProductTypes) {
      const itemType = (item.product_type || '').toLowerCase().trim();
      if (!rule.product_types.includes(itemType)) return false;
    }

    if (hasSpecificProducts) {
      if (!rule.specific_product_ids.includes(item.product_id)) return false;
    }

    return true;
  }

  function computeRuleTotal(cart, rule) {
    let total = 0;
    for (const item of cart.items) {
      const isGift = item.properties && item.properties._free_gift_rule;
      if (isGift) continue;
   if (itemMatchesRule(item, rule)) {
        total += (item.presentment_final_line_price ?? item.final_line_price) / 100;
      }
    }
    return total;
  }

  function findGiftLineByHandle(cart, handle) {
    return cart.items.find(
      item => item.properties && item.properties._free_gift_rule === handle
    );
  }

  function findLegacyGiftLine(cart) {
    return cart.items.find(item => {
      if (!item.properties) return false;
      if (item.properties._free_gift_rule) return false;
      const val = item.properties._free_gift;
      return val === 'true' || val === true;
    });
  }

function computeNonGiftTotal(cart) {
    let total = 0;
    for (const item of cart.items) {
      const isGift = item.properties && (item.properties._free_gift_rule || item.properties._free_gift === 'true' || item.properties._free_gift === true);
      if (!isGift) total += (item.presentment_final_line_price ?? item.final_line_price) / 100;
    }
    return total;
  }

  function refreshCartDrawer() {
    console.log('[GiftSystem] refreshCartDrawer: dispatching refresh event');
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
  }

  async function handleMultiGifts(cart, notice) {
    console.log('[GiftSystem] handleMultiGifts: called. isProcessingGifts=' + isProcessingGifts);
    if (isProcessingGifts) {
      console.warn('[GiftSystem] handleMultiGifts: BLOCKED — scheduling recheck');
      pendingRecheck = true;
      return;
    }

    const rules = getGiftRules(notice);

    if (!rules.length) {
      console.log('[GiftSystem] handleMultiGifts: no metaobject rules → falling back to legacy');
      await handleLegacyGift(cart, notice);
      return;
    }

    isProcessingGifts = true;
    console.log('[GiftSystem] handleMultiGifts: processing ' + rules.length + ' rules');

    try {
      const updates = {};
      let cartChanged = false;

      // ── Removals ──
      for (const rule of rules) {
        if (!rule.gift_variant_id) {
          console.warn('[GiftSystem] rule "' + rule.handle + '" has no gift_variant_id — skipping');
          continue;
        }
        const ruleTotal = computeRuleTotal(cart, rule);
        const shouldHaveGift = ruleTotal >= rule.threshold;
        const giftLine = findGiftLineByHandle(cart, rule.handle);
        console.log('[GiftSystem] rule "' + rule.handle + '": ruleTotal=' + ruleTotal + ' threshold=' + rule.threshold + ' shouldHaveGift=' + shouldHaveGift + ' hasGiftInCart=' + !!giftLine);
        if (!shouldHaveGift && giftLine) {
          console.log('[GiftSystem] → queuing REMOVAL for rule "' + rule.handle + '"');
          updates[giftLine.key] = 0;
          cartChanged = true;
        }
      }

      const enableGift = notice.dataset.enableGift === 'true';
      const giftVariantId = notice.dataset.giftVariantId;
      const giftThreshold = parseFloat(notice.dataset.giftThreshold) || 350;
      const nonGiftTotal = computeNonGiftTotal(cart);
      const legacyGiftLine = findLegacyGiftLine(cart);
      const shouldHaveLegacyGift = enableGift && !!giftVariantId && nonGiftTotal >= giftThreshold;
      console.log('[GiftSystem] legacy gift: enableGift=' + enableGift + ' giftVariantId=' + giftVariantId + ' nonGiftTotal=' + nonGiftTotal + ' giftThreshold=' + giftThreshold + ' shouldHaveLegacyGift=' + shouldHaveLegacyGift + ' hasLegacyInCart=' + !!legacyGiftLine);

      const giftVariantIdInt = parseInt(giftVariantId, 10);
      const allGiftCandidates = cart.items.filter(item => item.variant_id === giftVariantIdInt);
      if (allGiftCandidates.length > 0) {
        console.log("[GiftSystem] gift variant in cart — raw properties:", JSON.stringify(allGiftCandidates[0].properties));
      } else {
        console.log("[GiftSystem] gift variant NOT in cart by variant_id");
      }
      if (!shouldHaveLegacyGift && legacyGiftLine) {
        console.log('[GiftSystem] → queuing REMOVAL for legacy gift');
        updates[legacyGiftLine.key] = 0;
        cartChanged = true;
      }

      if (cartChanged) {
        console.log('[GiftSystem] → sending removals:', updates);
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        cachedCart = null;
        lastFetch = 0;
        cart = await getCart(false);
        cartChanged = true;
      }

      // ── Additions ──
      const itemsToAdd = [];

      for (const rule of rules) {
        if (!rule.gift_variant_id) continue;
        const ruleTotal = computeRuleTotal(cart, rule);
        const shouldHaveGift = ruleTotal >= rule.threshold;
        const giftLine = findGiftLineByHandle(cart, rule.handle);
        if (shouldHaveGift && !giftLine) {
          console.log('[GiftSystem] → queuing ADD for rule "' + rule.handle + '" variant=' + rule.gift_variant_id);
          itemsToAdd.push({
            id: parseInt(rule.gift_variant_id, 10),
            quantity: 1,
            properties: { _free_gift_rule: rule.handle, _free_gift: 'true' }
          });
        }
      }

      if (shouldHaveLegacyGift && !findLegacyGiftLine(cart)) {
        console.log('[GiftSystem] → queuing ADD for legacy gift variant=' + giftVariantId);
        itemsToAdd.push({
          id: parseInt(giftVariantId, 10),
          quantity: 1,
          properties: { _free_gift: 'true' }
        });
      }

      console.log('[GiftSystem] itemsToAdd:', itemsToAdd);

      if (itemsToAdd.length > 0) {
        // Add individually so one failure doesn't block the others
        for (const item of itemsToAdd) {
          try {
            const addRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [item] })
            });
            const addData = await addRes.json();
            if (addRes.ok) {
              console.log('[GiftSystem] ✅ Added variant ' + item.id + ' successfully');
              cachedCart = null;
              lastFetch = 0;
              cartChanged = true;
            } else {
              console.error('[GiftSystem] ❌ 422 failed to add variant ' + item.id + ' — response:', addData);
            }
          } catch (e) {
            console.error('[GiftSystem] ❌ Exception adding variant ' + item.id + ':', e);
          }
        }
      } else {
        console.log('[GiftSystem] No items to add — all gifts already correct');
      }

      if (cartChanged) {
        await new Promise(r => setTimeout(r, 500));
        refreshCartDrawer();
        await new Promise(r => setTimeout(r, 800));
        refreshCartDrawer();
        await new Promise(r => setTimeout(r, 300));
      }
      const freshCart = await getCart(false);
      disableGiftQuantitySelectors(freshCart);

    } catch (e) {
      console.error('[GiftSystem] handleMultiGifts ERROR:', e);
    } finally {
      isProcessingGifts = false;
      console.log('[GiftSystem] handleMultiGifts: done');
      if (pendingRecheck) {
        pendingRecheck = false;
        console.log('[GiftSystem] handleMultiGifts: running pending recheck');
        setTimeout(() => debouncedUpdate(true), 200);
      }
    }
  }

  async function handleLegacyGift(cart, notice) {
    const enableGift = notice.dataset.enableGift === 'true';
    const giftVariantId = notice.dataset.giftVariantId;
    if (!enableGift || !giftVariantId) {
      console.log('[LegacyGift] skipping — enableGift=' + enableGift + ' giftVariantId=' + giftVariantId);
      return;
    }

    const giftThreshold = parseFloat(notice.dataset.giftThreshold) || 350;
    const nonGiftTotal = computeNonGiftTotal(cart);
    const shouldHaveGift = nonGiftTotal >= giftThreshold;
    const giftLine = findLegacyGiftLine(cart);
    console.log('[LegacyGift] nonGiftTotal=' + nonGiftTotal + ' threshold=' + giftThreshold + ' shouldHaveGift=' + shouldHaveGift + ' hasGift=' + !!giftLine);

    if (shouldHaveGift && !giftLine) {
      console.log('[LegacyGift] → adding gift variant=' + giftVariantId);
      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: parseInt(giftVariantId, 10),
            quantity: 1,
            properties: { _free_gift: 'true' }
          })
        });
        const addData = await addRes.json();
        console.log('[LegacyGift] /cart/add.js response (ok=' + addRes.ok + '):', addData);
        if (addRes.ok) {
          cachedCart = null;
          lastFetch = 0;
          await new Promise(r => setTimeout(r, 300));
          refreshCartDrawer();
          await new Promise(r => setTimeout(r, 500));
          const freshCart = await getCart(false);
          disableGiftQuantitySelectors(freshCart);
        }
      } catch (e) {
        console.error('[LegacyGift] Add error:', e);
      }
    } else if (!shouldHaveGift && giftLine) {
      console.log('[LegacyGift] → removing gift');
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: { [giftLine.key]: 0 } })
        });
        cachedCart = null;
        lastFetch = 0;
        await new Promise(r => setTimeout(r, 300));
        refreshCartDrawer();
      } catch (e) {
        console.error('[LegacyGift] Remove error:', e);
      }
    } else {
      console.log('[LegacyGift] no action needed');
    }
  }

  function disableGiftQuantitySelectors(cart) {
    if (disableInProgress) return;
    disableInProgress = true;

    if (!cart || !cart.items) { disableInProgress = false; return; }

    const giftItems = cart.items.filter(
      item => item.properties && (item.properties._free_gift_rule || item.properties._free_gift === 'true' || item.properties._free_gift === true)
    );

    if (giftItems.length === 0) { disableInProgress = false; return; }

    setTimeout(() => {
      const cartItemEls = document.querySelectorAll('.cart-item');

      giftItems.forEach(giftItem => {
        cartItemEls.forEach(itemEl => {
          const variantId = itemEl.dataset.variantId || itemEl.getAttribute('data-variant-id');
          if (variantId != giftItem.variant_id) return;

          const qtyWrapper = itemEl.querySelector('quantity-input, .quantity-selector');
          if (qtyWrapper) { qtyWrapper.style.opacity = '0.4'; qtyWrapper.style.pointerEvents = 'none'; }

          const qtyInput = itemEl.querySelector('input[type="number"]');
          if (qtyInput) { qtyInput.disabled = true; qtyInput.readOnly = true; qtyInput.value = '1'; qtyInput.style.opacity = '0.4'; }

          itemEl.querySelectorAll('button').forEach(btn => {
            if (!btn.textContent.toLowerCase().includes('remove')) {
              btn.disabled = true; btn.style.opacity = '0.4'; btn.style.pointerEvents = 'none';
            }
          });

          if (!itemEl.querySelector('.free-gift-badge')) {
            const imageWrapper = itemEl.querySelector('.cart-item__image, .media, [class*="image"]');
            if (imageWrapper) {
              imageWrapper.style.position = 'relative';
              const badge = document.createElement('span');
              badge.className = 'free-gift-badge';
              badge.style.cssText = 'position: absolute; top: 58%; left: 50%; transform: translate(-50%, -50%); background: rgb(41, 55, 58); color: white; padding: 4px 8px; border-radius: 4px; font-size: 10.5px; font-weight: bold; line-height: 1em; z-index: 2; white-space: nowrap;';
              badge.textContent = 'FREE GIFT';
              imageWrapper.appendChild(badge);
            }
          }
        });
      });

      disableInProgress = false;
    }, 300);
  }

  function confetti(wrapper) {
    const colors = ['#EBFDFF', '#283638', '#4CAF50', '#FFD700'];
    for (let i = 0; i < 12; i++) {
      const d = document.createElement('div');
      d.className = 'confetti';
      d.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      const angle = (Math.PI * 2 * i) / 12;
      const v = 30 + Math.random() * 20;
      d.style.setProperty('--x', `${Math.cos(angle) * v}px`);
      d.style.setProperty('--y', `${Math.sin(angle) * v}px`);
      d.style.left = '15px'; d.style.top = '15px';
      d.style.animation = `confettiBurst ${0.6 + Math.random() * 0.3}s ease-out forwards`;
      wrapper.appendChild(d);
      setTimeout(() => d.remove(), 1000);
    }
  }

  function triggerAnimation(threshold) {
    const notice = getNotice();
    if (!notice) return;
    const wrapper = notice.querySelector(`[data-wrapper="${threshold}"]`);
    const icon = notice.querySelector(`.fs-icon[data-threshold="${threshold}"]`);
    if (!icon || !wrapper) return;
    icon.classList.add('just-unlocked');
    setTimeout(() => icon.classList.remove('just-unlocked'), 500);
    confetti(wrapper);
  }

  function setProgress(notice, percent) {
    const bar = notice.querySelector('.fs-bar-progress');
    if (!bar) return;
    const clamped = Math.min(100, Math.max(0, Number(percent) || 0));
    const diff = Math.abs(clamped - currentProgress);
    if (clamped >= currentProgress || diff > 2) {
      currentProgress = clamped;
      requestAnimationFrame(() => {
        notice.style.setProperty('--cart-progress', clamped + '%');
      });
    }
  }

  function updateIcon(notice, threshold, isUnlocked) {
    const wrapper = notice.querySelector(`[data-wrapper="${threshold}"]`);
    if (!wrapper) return;
    const icon = wrapper.querySelector('.fs-icon');
    const amount = wrapper.querySelector('.fs-amount');
    if (!icon) return;
    if (isUnlocked) {
      icon.classList.add('is-unlocked');
      icon.innerHTML = `<img src="${ICONS.unlocked}" width="15" height="15" alt="Unlocked">`;
      if (amount) amount.classList.add('is-hidden');
    } else {
      icon.classList.remove('is-unlocked');
      icon.innerHTML = `<img src="${ICONS[threshold]}" width="15" height="15" alt="${threshold}">`;
      if (amount) amount.classList.remove('is-hidden');
    }
  }

  function updateMessage(notice, total, thresholds, unlocked, enableGift) {
    const messageEl = notice.querySelector('.fs-text');
    if (!messageEl) return;
    let message = '';
    if (enableGift) {
      const giftIsFirst = thresholds.gift < thresholds.standard;
      if (unlocked.gift && unlocked.express && unlocked.standard) {
        message = '🎉 All rewards unlocked! <strong>Complete your order to redeem</strong>';
      } else if (unlocked.gift && unlocked.express) {
        const deficit = Math.max(0, Math.round(thresholds.standard - total));
        message = `🎉 Almost there! Only <strong>$${deficit}</strong> away from free standard shipping`;
      } else if (unlocked.gift) {
        if (unlocked.standard) {
          const deficit = Math.max(0, Math.round(thresholds.express - total));
          message = `🎉 Free Gift unlocked! Only <strong>$${deficit}</strong> away from free express shipping`;
        } else {
          const nextThreshold = thresholds.express < thresholds.standard ? thresholds.express : thresholds.standard;
          const nextLabel = thresholds.express < thresholds.standard ? 'free express shipping' : 'free standard shipping';
          const deficit = Math.max(0, Math.round(nextThreshold - total));
          message = `🎉 Free Gift unlocked! Only <strong>$${deficit}</strong> away from ${nextLabel}`;
        }
      } else if (unlocked.express) {
        const deficit = Math.max(0, Math.round(thresholds.gift - total));
        message = `Spend <strong>$${deficit} more</strong> to unlock <strong>a free gift</strong>`;
      } else if (unlocked.standard) {
        const nextThreshold = thresholds.gift < thresholds.express ? thresholds.gift : thresholds.express;
        const nextLabel = thresholds.gift < thresholds.express ? 'a free gift' : 'free express shipping';
        const deficit = Math.max(0, Math.round(nextThreshold - total));
        message = `🎉 Standard shipping unlocked! Only <strong>$${deficit}</strong> away from ${nextLabel}`;
      } else if (giftIsFirst) {
        const deficit = Math.max(0, Math.round(thresholds.gift - total));
        message = `Spend <strong>$${deficit} more</strong> to unlock <strong>a free gift</strong>`;
      } else {
        const deficit = Math.max(0, Math.round(thresholds.standard - total));
        message = `Spend <strong>$${deficit} more</strong> to unlock <strong>free standard shipping</strong>`;
      }
    } else {
      if (unlocked.express) {
        message = '🎉 Free Express Shipping unlocked! <strong>Complete your order</strong>';
      } else if (unlocked.standard) {
        const deficit = Math.max(0, Math.round(thresholds.express - total));
        message = `🎉 Standard shipping unlocked! Only <strong>$${deficit}</strong> away from free express shipping`;
      } else {
        const deficit = Math.max(0, Math.round(thresholds.standard - total));
        message = `Spend <strong>$${deficit} more</strong> to unlock <strong>free standard shipping</strong>`;
      }
    }
    messageEl.innerHTML = message;
  }

  function initializeFromServerState(notice) {
    const bar = notice.querySelector('.fs-bar-progress');
    if (bar && currentProgress > 0) {
      bar.style.transition = 'none';
      notice.style.setProperty('--cart-progress', currentProgress + '%');
      requestAnimationFrame(() => { bar.style.transition = ''; });
    }
    if (currentProgress === 0) {
      currentProgress = parseFloat(notice.dataset.initialProgress) || 0;
    }
    const standardIcon = notice.querySelector('.fs-icon[data-threshold="standard"]');
    const expressIcon  = notice.querySelector('.fs-icon[data-threshold="express"]');
    const giftIcon     = notice.querySelector('.fs-icon[data-threshold="gift"]');
    previousUnlockStates = {
      standard: standardIcon ? standardIcon.classList.contains('is-unlocked') : false,
      express:  expressIcon  ? expressIcon.classList.contains('is-unlocked')  : false,
      gift:     giftIcon     ? giftIcon.classList.contains('is-unlocked')     : false
    };
  }

  async function updateNotice(forceGiftCheck) {
    console.log('[GiftSystem] updateNotice: forceGiftCheck=' + forceGiftCheck + ' updateInProgress=' + updateInProgress);
    if (updateInProgress) {
      console.warn('[GiftSystem] updateNotice: BLOCKED — already in progress');
      return;
    }
    updateInProgress = true;

    const notice = getNotice();
    if (!notice) {
      console.error('[GiftSystem] updateNotice: NO NOTICE ELEMENT FOUND');
      updateInProgress = false;
      return;
    }

    try {
      const cart = await getCart(!forceGiftCheck);

      if (!cart || cart.item_count === 0) {
        notice.dataset.cartEmpty = '0';
        currentProgress = 0;
        const messageEl = notice.querySelector('.fs-text');
        if (messageEl) messageEl.innerHTML = '🎁 <strong>Add an item to cart to start unlocking rewards</strong>';
        const sub = notice.querySelector('.fs-subtext');
        if (sub) sub.style.display = 'none';
        const barWrap = notice.querySelector('.fs-bar-wrapper');
        if (barWrap) barWrap.style.display = 'none';
        previousUnlockStates = { standard: false, express: false, gift: false };
        cachedCart = null;
        updateInProgress = false;
        return;
      }

      notice.dataset.cartEmpty = String(cart.item_count);
      const barWrap = notice.querySelector('.fs-bar-wrapper');
      if (barWrap) barWrap.style.display = '';
      const sub = notice.querySelector('.fs-subtext');
      if (sub) sub.style.display = '';

      const nonGiftTotal = computeNonGiftTotal(cart);
      console.log('[GiftSystem] updateNotice: nonGiftTotal=' + nonGiftTotal);

      const thresholds = {
        standard: parseFloat(notice.dataset.standardThreshold) || 150,
        express:  parseFloat(notice.dataset.expressThreshold)  || 250,
        gift:     parseFloat(notice.dataset.giftThreshold)     || 350
      };

      const enableGift = notice.dataset.enableGift === 'true';

      const unlocked = {
        standard: nonGiftTotal >= thresholds.standard,
        express:  nonGiftTotal >= thresholds.express,
        gift:     nonGiftTotal >= thresholds.gift
      };

      if (unlocked.standard && !previousUnlockStates.standard) triggerAnimation('standard');
      if (unlocked.express  && !previousUnlockStates.express)  triggerAnimation('express');
      if (enableGift && unlocked.gift && !previousUnlockStates.gift) triggerAnimation('gift');

      previousUnlockStates = { ...unlocked };

const maxThreshold = enableGift
        ? Math.max(thresholds.express, thresholds.gift)
        : thresholds.express;      setProgress(notice, (nonGiftTotal / maxThreshold) * 100);
      updateMessage(notice, nonGiftTotal, thresholds, unlocked, enableGift);
      updateIcon(notice, 'standard', unlocked.standard);
      updateIcon(notice, 'express',  unlocked.express);
      if (enableGift) updateIcon(notice, 'gift', unlocked.gift);

      if (forceGiftCheck) {
        console.log('[GiftSystem] updateNotice: calling handleMultiGifts');
        await handleMultiGifts(cart, notice);
      } else {
        disableGiftQuantitySelectors(cart);
      }

    } catch (e) {
      console.error('[GiftSystem] updateNotice ERROR:', e);
    } finally {
      updateInProgress = false;
    }
  }

  function debouncedUpdate(forceGiftCheck) {
    if (forceGiftCheck) pendingForce = true;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const force = pendingForce;
      pendingForce = false;
      debounceTimer = null;
      console.log('[GiftSystem] debouncedUpdate firing: force=' + force);
      const notice = getNotice();
      if (notice) {
        cachedCart = null;
        initializeFromServerState(notice);
        updateNotice(force);
      } else {
        console.error('[GiftSystem] debouncedUpdate: no notice found');
      }
    }, UPDATE_DEBOUNCE);
  }

  // ── Fetch interceptor ──
  // Catches ALL cart mutations: add-to-cart from product page, in-cart upsells, qty changes
  // isProcessingGifts guard prevents our own gift API calls from looping back
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : '';

    if (isProcessingGifts) {
      return originalFetch.apply(this, args);
    }

    const isCartMutation = (
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update')
    );

    if (isCartMutation) {
      console.log('[GiftSystem] fetch interceptor caught cart mutation: ' + url);
      return originalFetch.apply(this, args).then(response => {
        console.log('[GiftSystem] cart mutation response ok=' + response.ok + ' url=' + url);
        if (response.ok) {
          cachedCart = null;
          lastFetch = 0;
          // Wait for theme to finish re-rendering before running gift check
          setTimeout(() => {
            console.log('[GiftSystem] fetch interceptor: triggering debouncedUpdate after 600ms');
            debouncedUpdate(true);
          }, 600);
        }
        return response;
      });
    }

    return originalFetch.apply(this, args);
  };

  document.addEventListener('on:line-item:change', () => {
    console.log('[GiftSystem] on:line-item:change fired');
    if (isProcessingGifts) return;
    debouncedUpdate(true);
  });

  const cartDrawerObserver = new MutationObserver(() => {
    const notice = getNotice();
    if (notice && !notice.dataset.initialized) {
      notice.dataset.initialized = 'true';
      console.log('[GiftSystem] MutationObserver: new notice detected, initializing');
      initializeFromServerState(notice);
      updateNotice(false);
    }
  });

  const cartDrawer = document.getElementById('cart-drawer');
  if (cartDrawer) {
    cartDrawerObserver.observe(cartDrawer, { childList: true, subtree: true });
  }

  function startPolling() {
    if (pollInterval) clearInterval(pollInterval);
    pollInterval = setInterval(() => {
      const notice = getNotice();
      if (notice) updateNotice(false);
    }, 10000);
  }

  startPolling();

  const initialNotice = getNotice();
  if (initialNotice) {
    initialNotice.dataset.initialized = 'true';
    initializeFromServerState(initialNotice);
    updateNotice(true);
  }

})();